import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Bell, CheckCheck, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useSystemNotifications, useRecentAuditLogs } from "@/hooks/use-notifications";
import { useAuth } from "@/hooks/use-auth";
import { NotificationItem } from "./NotificationItem";

export function NotificationDropdown() {
  const [activeTab, setActiveTab] = useState("system");
  const { data: systemNotifications = [] } = useSystemNotifications();
  const { activeCompany } = useAuth();
  const { data: auditLogs = [] } = useRecentAuditLogs(activeCompany?.id);

  const unreadSystemCount = systemNotifications.filter(n => !n.is_read).length;
  const recentAuditCount = auditLogs.length;

  return (
    <div className="p-0">
      <div className="flex items-center justify-between p-4 pb-2">
        <h3 className="font-semibold">Notificações</h3>
        <Bell className="h-4 w-4 text-muted-foreground" />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mx-4 mb-2 h-8">
          <TabsTrigger value="system" className="relative flex items-center justify-center gap-1 px-2 py-1 text-sm">
            <span>Sistema</span>
            {unreadSystemCount > 0 && (
              <Badge variant="destructive" className="h-3 w-3 rounded-full p-0 text-[10px] flex items-center justify-center ml-1">
                {unreadSystemCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity" className="relative flex items-center justify-center gap-1 px-2 py-1 text-sm">
            <span>Atividades</span>
            {recentAuditCount > 0 && (
              <Badge variant="secondary" className="h-3 w-3 rounded-full p-0 text-[10px] flex items-center justify-center ml-1">
                {recentAuditCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="mt-0">
          <ScrollArea className="h-[300px]">
            {systemNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mb-2" />
                <p className="text-sm">Nenhuma notificação do sistema</p>
              </div>
            ) : (
              <div className="space-y-0">
                {systemNotifications.map((notification, index) => (
                  <div key={notification.id}>
                    <NotificationItem notification={notification} />
                    {index < systemNotifications.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          <ScrollArea className="h-[300px]">
            {auditLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <Activity className="h-8 w-8 mb-2" />
                <p className="text-sm">Nenhuma atividade recente</p>
              </div>
            ) : (
              <div className="space-y-0">
                {auditLogs.map((log: any, index) => (
                  <div key={log.id}>
                    <div className="p-3 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Activity className="h-3 w-3 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-tight">
                            {getActivityDescription(log.action, log.entity_type)}
                          </p>
                          {log.profiles?.full_name && (
                            <p className="text-xs text-muted-foreground mt-1">
                              por {log.profiles.full_name}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDistanceToNow(new Date(log.created_at), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                    {index < auditLogs.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function getActivityDescription(action: string, entityType: string): string {
  const actionMap: Record<string, string> = {
    "user.create": "Novo usuário criado",
    "user.invite": "Usuário convidado",
    "vehicle.create": "Novo veículo cadastrado",
    "driver.create": "Novo motorista cadastrado",
    "trip.create": "Nova viagem criada",
    "work_order.create": "Nova ordem de serviço criada",
    "plan.upgrade": "Plano atualizado",
    "plan.downgrade": "Plano rebaixado",
    "ticket.message": "Nova mensagem em ticket",
    "ticket.status_change": "Status de ticket alterado",
    "ticket.assignment": "Ticket atribuído",
    "ticket.priority_change": "Prioridade de ticket alterada",
  };

  return actionMap[action] || `${action} - ${entityType}`;
}